import React,
{
  useState,
  useEffect,
  useCallback
} from "react";
import { Link,useNavigate } from "react-router-dom";
import "../styles/Pricing.css";


/* ─── API URL ─── */
const API_URL = process.env.REACT_APP_API_URL;

/* ─── Icons ─── */
const ConvertIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z"/>
    <polyline points="14 2 14 8 20 8"/>
    <path d="M12 18v-6"/>
    <path d="M9 15l3-3 3 3"/>
  </svg>
);

const CheckIcon = ({ color = "#2DD4BF" }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ─── Data ─── */
const PLANS = [
  {
    name: "Free",
    price: "₹0",
    annualPrice: "₹0",
    period: "/month",
    desc: "Perfect for trying ConvertPro",
    features: [
      "2 conversions/day",
      "5 MB file size",
      "History (last 5 conversions)",
      "CSV, JSON, XML, XLSX, TXT, YAML",
      "HL7 conversion",
      "Basic file conversion",
    ],
    cta: "Included",
    enabled: true,
    popular: false,
  },

  {
    name: "Basic",
    price: "₹99",
    annualPrice: "₹79",
    period: "/month",
    desc: "Best for regular users",
    features: [
      "100 conversions/day",
      "50 MB file size",
      "30 days history",
      "CSV cleaning & repair",
      "CSV → SQL conversion",
      "PDF & DOCX support",
      "HL7 & EDI conversion",
    ],
    cta: "Get Started",
    enabled: true,
    popular: false,
  },

  {
    name: "Standard",
    price: "₹499",
    annualPrice: "₹399",
    period: "/month",
    desc: "Most popular for professionals",
    features: [
      "500 conversions/day",
      "100 MB file size",
      "60 days history",
      "Advanced CSV processing",
      "Smart SQL type detection",
      "Parquet & Avro support",
      "Batch conversion",
      "PDF extraction",
    ],
    cta: "Coming Soon",
    enabled: false,
    popular: false,
  },

  {
    name: "Premium",
    price: "₹999",
    annualPrice: "₹799",
    period: "/month",
    desc: "Advanced data processing",
    features: [
      "Unlimited conversions",
      "10 GB file size",
      "Unlimited history",
      "Parquet, Avro, ORC",
      "Audio processing",
      "Video processing",
      "Advanced extraction",
    ],
    cta: "Coming Soon",
    enabled: false,
    popular: false,
  },

  {
    name: "Enterprise",
    price: "₹1,999",
    annualPrice: "₹1,599",
    period: "/month",
    desc: "For businesses & integrations",
    features: [
      "Unlimited conversions",
      "Unlimited file size",
      "API access",
      "AI-powered extraction",
      "Audio/video insights",
      "Custom integrations",
      "Priority support",
    ],
    cta: "Coming Soon",
    enabled: false,
    popular: false,
  },
];

/* ─── Navbar ─── */
function Navbar() {
  return (
    <nav className="pp-navbar">
      <div className="pp-navbar-inner">
        <div className="pp-logo">
          <div className="pp-logo-icon">
            <ConvertIcon />
          </div>
          <span className="pp-logo-text">ConvertPro</span>
        </div>
        <div className="pp-nav-links">
          <a href="/" className="pp-nav-link">Home</a>
          <a href="/feedback" className="pp-nav-link">Feedback</a>
          <a href="/pricing" className="pp-nav-link pp-nav-link--active">Pricing</a>
          <a href="/contact" className="pp-nav-link">Contact</a>
        </div>
        <div className="pp-nav-actions">
          <a href="/login" className="pp-nav-signin">Sign In</a>
          <a href="/signup" className="pp-nav-cta">Get Started Free</a>
        </div>
      </div>
    </nav>
  );
}

/* ─── Main ─── */
export default function ConvertProPublicPricing() {
  const [annual, setAnnual] = useState(false);
  const navigate = useNavigate();

  const handlePurchase = useCallback( async (planName) => {

      try {

        const currentUser = JSON.parse(
          localStorage.getItem("currentUser")
        );

        if (!currentUser) {

          alert(
            "Please login before purchasing."
          );

          navigate("/login");

          return;
        }

        const response = await fetch(
          `${API_URL}/create-order`,
          {
            method: "POST",
            headers: {
              "Content-Type":
                "application/json"
            },
            body: JSON.stringify({
              plan: planName
            })
          }
        );

        const order = await response.json();

        const options = {

          key: order.key,

          amount: order.amount,

          currency: order.currency,

          order_id: order.order_id,

          name: "ConvertPro",

          description: `${planName} Subscription`,

          handler: async function (
            paymentResponse
          ) {

            const verifyResponse =
              await fetch(
                `${API_URL}/verify-payment`,
                {
                  method: "POST",
                  headers: {
                    "Content-Type":
                      "application/json"
                  },
                  body: JSON.stringify({

                    ...paymentResponse,

                    user_id:
                      currentUser.user_id,

                    plan: planName,

                    amount:
                      order.amount / 100

                  })
                }
              );

            const verifyData =
              await verifyResponse.json();

            if (verifyResponse.ok) {

              alert(
                "Payment Successful 🎉"
              );

              window.location.href =
                "/dashboard";

            } else {

              alert(
                verifyData.detail ||
                "Payment verification failed"
              );

            }
          },

          theme: {
            color: "#2563eb"
          }

        };

        const razorpay =
          new window.Razorpay(options);

        razorpay.open();

      } catch (error) {

        console.error(error);

        alert(
          "Unable to process payment"
        );

      }

    },
    [navigate]
);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {

        const selectedPlan =
          localStorage.getItem(
            "selectedPlan"
          );

        const currentUser = JSON.parse(
          localStorage.getItem(
            "currentUser"
          )
        );

        if (selectedPlan && currentUser) {

          if (selectedPlan === "Free") {
            localStorage.removeItem("selectedPlan");
            return;
          }

          if (selectedPlan === "Enterprise") {
            navigate("/contact");
            localStorage.removeItem("selectedPlan");
            return;
          }

          handlePurchase(selectedPlan);

          localStorage.removeItem("selectedPlan");
        }

      }, [handlePurchase, navigate]);

  const handleGetStarted = (planName) => {

      if (planName === "Free") {
      navigate("/signup");
      return;
      }

    const currentUser = JSON.parse(
      localStorage.getItem("currentUser")
    );

    if (!currentUser) {

      localStorage.setItem(
        "selectedPlan",
        planName
      );

      navigate("/login");

      return;
    }

    handlePurchase(planName);
  };



  return (
    <div className="pp-page">
      <Navbar />

            <main className="pp-main">
              {/* Hero */}
              <section className="pp-hero">
                <div className="pp-hero-badge">
                  <span>SECURE, HIGH-SPEED FILE CONVERSION</span>
                </div>
                <h1 className="pp-title">
                  Convert Any File Format in Seconds
                </h1>

                <p className="pp-subtitle">
                  CSV, JSON, XML, HL7, EDI, SQL, PDF, Parquet, Avro and 50+ formats.
                  Choose a plan that grows with your needs.
                </p>

                {/* Toggle */}
                <div className="pp-toggle-row">
                  <span className={`pp-toggle-label ${!annual ? "pp-toggle-label--active" : ""}`}>
                    Monthly
                  </span>
                  <button
                    className={`pp-toggle ${annual ? "pp-toggle--on" : ""}`}
                    onClick={() => setAnnual(!annual)}
                    aria-label="Toggle annual billing"
                  >
                    <span className="pp-toggle-knob" />
                  </button>
                  <span className={`pp-toggle-label ${annual ? "pp-toggle-label--active" : ""}`}>
                    Annual
                  </span>
                  {annual && <span className="pp-save-badge">Save 20%</span>}
                </div>
              </section>
              <div className="pp-trust-badges">
                <span>✅ Secure Processing</span>
                <span>✅ Auto File Deletion</span>
                <span>✅ No Credit Card Required</span>
                <span>✅ 50+ Formats Supported</span>
              </div>

              {/* Plan Cards */}
              <div className="pp-plans-grid">
                {PLANS.map((plan) => (
                  <div
                    key={plan.name}
                    className={`pp-plan-card ${
                      plan.popular ? "pp-plan-card--popular" : ""
                    } ${!plan.enabled ? "pp-plan-card--disabled" : ""}`}
                  >

                    {plan.name === "Free" && (
                      <span className="pp-badge-free">
                        DEFAULT PLAN
                      </span>
                    )}

                    {plan.popular && (
                      <span className="pp-badge-popular">
                        ⭐ MOST POPULAR
                      </span>
                    )}

                    {!plan.enabled && (
                      <span className="pp-badge-soon">
                        Coming Soon
                      </span>
                    )}

                    <h3 className="pp-plan-name">
                      {plan.name}
                    </h3>
                    <div className="pp-price-row">
                      <span className="pp-price">{annual ? plan.annualPrice : plan.price}</span>
                      <span className="pp-period">{plan.period}</span>
                    </div>
                    <p className="pp-plan-desc">{plan.desc}</p>

                    <div className="pp-plan-divider" />

                    <div className="pp-feature-tags">
                      {plan.name === "Basic" && (
                        <>
                          <span className="pp-tag">Data Cleaning</span>
                          <span className="pp-tag">EDI</span>
                        </>
                      )}

                      {plan.name === "Standard" && (
                        <>
                          <span className="pp-tag">Batch</span>
                          <span className="pp-tag">Big Data</span>
                        </>
                      )}

                      {plan.name === "Premium" && (
                        <>
                          <span className="pp-tag">Audio</span>
                          <span className="pp-tag">Video</span>
                        </>
                      )}

                      {plan.name === "Enterprise" && (
                        <>
                          <span className="pp-tag">AI</span>
                          <span className="pp-tag">API</span>
                        </>
                      )}
                    </div>

                    <ul className="pp-feature-list">
                      {plan.features.map((f) => (
                        <li key={f} className="pp-feature-item">
                          <CheckIcon color={plan.enabled ? "#0D9488" : "#CBD5E1"} /> {f}
                        </li>
                      ))}
                    </ul>

                    {plan.name === "Free" ? (
                      <button
                        className="pp-btn-free"
                        disabled
                      >
                        Included by Default
                      </button>
                    ) : (
                      <button
                        className={
                          plan.enabled
                            ? "pp-btn-primary"
                            : "pp-btn-disabled"
                        }
                        disabled={!plan.enabled}
                        onClick={() => {
                          if (!plan.enabled) return;

                          if (plan.name === "Enterprise") {
                            navigate("/contact");
                            return;
                          }

                          handleGetStarted(plan.name);
                        }}
                      >
                                                {plan.cta}
                                              </button>
                                            )}

                                            {/* Available for every plan, including Coming Soon */}
                                            <Link
                                              to={`/pricing/${plan.name.toLowerCase()}`}
                                              className="pp-plan-details-link"
                                              aria-label={`View ${plan.name} plan details`}
                                            >
                                              View plan details <span aria-hidden="true">→</span>
                                            </Link>

                                          </div>
                                        ))}
                                      </div>
              <section className="pp-comparison">
                <h2>Compare Plans</h2>

                <div className="pp-table-wrapper">
                  <table className="pp-compare-table">
                    <thead>
                      <tr>
                        <th>Feature</th>
                        <th>Free</th>
                        <th>Basic</th>
                        <th>Standard</th>
                        <th>Premium</th>
                        <th>Enterprise</th>
                      </tr>
                    </thead>

                    <tbody>
                      <tr>
                        <td>Conversions</td>
                        <td>2/day</td>
                        <td>100/day</td>
                        <td>500/day</td>
                        <td>Unlimited</td>
                        <td>Unlimited</td>
                      </tr>

                      <tr>
                        <td>File Size</td>
                        <td>5 MB</td>
                        <td>50 MB</td>
                        <td>100 MB</td>
                        <td>10 GB</td>
                        <td>Unlimited</td>
                      </tr>

                      <tr>
                        <td>History</td>
                        <td>5</td>
                        <td>30 Days</td>
                        <td>60 Days</td>
                        <td>Unlimited</td>
                        <td>Unlimited</td>
                      </tr>

                      <tr>
                        <td>HL7</td>
                        <td>✅</td>
                        <td>✅</td>
                        <td>✅</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>

                      <tr>
                        <td>EDI</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>

                      <tr>
                        <td>CSV Cleaning</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>

                      <tr>
                        <td>Batch Conversion</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>

                      <tr>
                        <td>Parquet / Avro</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>

                      <tr>
                        <td>Audio / Video</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>✅</td>
                        <td>✅</td>
                      </tr>

                      <tr>
                        <td>AI Processing</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>✅</td>
                      </tr>

                      <tr>
                        <td>API Access</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>❌</td>
                        <td>✅</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
              {/* Trust */}
              <p className="pp-trust-text">
                All plans include SSL encryption, GDPR compliance, and automatic file deletion after processing.
              </p>
            </main>

      {/* Footer */}
      <footer className="pp-footer">
        <div className="pp-footer-inner">
          <div className="pp-footer-brand">
            <div className="pp-logo">
              <div className="pp-logo-icon pp-logo-icon-sm">
                <ConvertIcon />
              </div>
              <span className="pp-logo-text">ConvertPro</span>
            </div>
          </div>
          <p className="pp-footer-copy">&copy; 2026 ConvertPro. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
